import React, { useState, useEffect, useMemo } from 'react';
import {
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Button,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Box,
    TextField,
    OutlinedInput,
    Checkbox,
    ListItemText,
    CircularProgress,
    Slide,
} from '@mui/material';
import { useSelector } from 'react-redux';
import { useTheme } from '@mui/material/styles';
import { useGetSitesQuery, useGetDevicesQuery, useGetAllUsersQuery } from '../../services/api';

const Transition = React.forwardRef(function Transition(props, ref) {
    return <Slide direction="up" ref={ref} {...props} />;
});

const EditGroupDialogBox = ({ open, onClose, onSave, isUpdating, group }) => {
    const theme = useTheme();
    const { company } = useSelector((state) => state.global);

    const [groupName, setGroupName] = useState('');
    const [selectedSites, setSelectedSites] = useState([]);
    const [selectedDevices, setSelectedDevices] = useState([]);
    const [selectedUsers, setSelectedUsers] = useState([]);

    // Fetch group data
    useEffect(() => {
        if (group) {
            setGroupName(group.name || '');
            setSelectedSites(group.sites?.map((s) => s._id) || []);
            setSelectedDevices(group.devices?.map((d) => d._id) || []);
            setSelectedUsers(group.users?.map((u) => u._id) || []);
        }
    }, [group]);

    // API
    const { data: sitesData } = useGetSitesQuery(company);
    const { data: devicesData } = useGetDevicesQuery(company);
    const { data: usersData } = useGetAllUsersQuery(company);

    // Filter data
    const filteredSites = useMemo(() => {
        return sitesData?.filter((site) => site.company._id === company && site.enabled) || [];
    }, [sitesData, company]);

    const filteredDevices = useMemo(() => {
        return devicesData?.filter((device) => device.company._id === company && device.addedToDashboard) || [];
    }, [devicesData, company]);

    const filteredUsers = useMemo(() => {
        return usersData?.users?.filter((user) => user.company?._id === company) || [];
    }, [usersData, company]);

    // Handle save
    const handleSave = () => {
        onSave({
            _id: group._id,
            name: groupName,
            sites: selectedSites,
            devices: selectedDevices,
            users: selectedUsers,
            company,
        });
    };

    return (
        <Dialog
            open={open}
            TransitionComponent={Transition}
            onClose={onClose}
            PaperProps={{
                sx: { backgroundColor: theme.palette.background.paper },
            }}
        >
            <DialogTitle variant="h2" fontWeight="bold">
                Edit Group
            </DialogTitle>
            <DialogContent sx={{ width: '500px', padding: '20px' }}>
                <Box display="flex" flexDirection="column" gap={2} sx={{ mt: '1rem' }}>
                    <TextField label="Group Name" variant="outlined" value={groupName} onChange={(e) => setGroupName(e.target.value)} fullWidth />

                    {/* Sites */}
                    <FormControl fullWidth>
                        <InputLabel>Select Sites</InputLabel>
                        <Select
                            multiple
                            value={selectedSites}
                            onChange={(e) => setSelectedSites(e.target.value)}
                            input={<OutlinedInput label="Select Sites" />}
                            renderValue={(selected) => selected.map((id) => filteredSites.find((s) => s._id === id)?.name || id).join(', ')}
                        >
                            {filteredSites.map((site) => (
                                <MenuItem key={site._id} value={site._id}>
                                    <Checkbox checked={selectedSites.includes(site._id)} />
                                    <ListItemText primary={site.name} />
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    {/* Devices */}
                    <FormControl fullWidth>
                        <InputLabel>Select Devices</InputLabel>
                        <Select
                            multiple
                            value={selectedDevices}
                            onChange={(e) => setSelectedDevices(e.target.value)}
                            input={<OutlinedInput label="Select Devices" />}
                            renderValue={(selected) => selected.map((id) => filteredDevices.find((d) => d._id === id)?.name || id).join(', ')}
                        >
                            {filteredDevices.map((device) => (
                                <MenuItem key={device._id} value={device._id}>
                                    <Checkbox checked={selectedDevices.includes(device._id)} />
                                    <ListItemText primary={device.name} />
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    {/* Users */}
                    <FormControl fullWidth>
                        <InputLabel>Select Users</InputLabel>
                        <Select
                            multiple
                            value={selectedUsers}
                            onChange={(e) => setSelectedUsers(e.target.value)}
                            input={<OutlinedInput label="Select Users" />}
                            renderValue={(selected) => selected.map((id) => filteredUsers.find((u) => u._id === id)?.name || id).join(', ')}
                        >
                            {filteredUsers.map((user) => (
                                <MenuItem key={user._id} value={user._id}>
                                    <Checkbox checked={selectedUsers.includes(user._id)} />
                                    <ListItemText primary={user.name} />
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Box>
            </DialogContent>
            <DialogActions sx={{ padding: '16px 24px' }}>
                <Button
                    onClick={onClose}
                    variant="outlined"
                    sx={{
                        color: theme.palette.text.black,
                        backgroundColor: theme.palette.text.white,
                        '&:hover': {
                            backgroundColor: theme.palette.text.white,
                        },
                        '&:focus': { outline: 'none' },
                    }}
                >
                    Cancel
                </Button>
                <Button
                    onClick={handleSave}
                    variant="contained"
                    disabled={isUpdating}
                    sx={{
                        color: theme.palette.text.black,
                        backgroundColor: theme.palette.text.white,
                        '&:hover': {
                            backgroundColor: theme.palette.text.white,
                        },
                        '&:focus': { outline: 'none' },
                    }}
                >
                    {isUpdating ? <CircularProgress size={24} /> : 'Save'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default EditGroupDialogBox;
